import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Phone } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";
import { useChat } from "../context/ChatContext";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/Avatar";
import { ru } from "../locales/ru";

dayjs.extend(relativeTime);
dayjs.locale("ru");

function preview(chat, meId) {
  const lm = chat.last_message;
  if (!lm) return ru.chats.noMessages;
  const prefix = lm.sender_id === meId ? `${ru.chats.you}: ` : chat.type === "global" ? `${lm.sender_name}: ` : "";
  return prefix + lm.text;
}

export default function Chats() {
  const { chats, online, loadChats } = useChat();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  return (
    <div className="pt-8 pb-28" data-testid="chats-page">
      <div className="flex items-center justify-between px-6 mb-4">
        <h1 className="text-3xl font-extrabold tracking-tight">{ru.nav.chats}</h1>
        <button
          data-testid="calls-link-button"
          onClick={() => navigate("/calls")}
          className="flex items-center gap-1.5 text-sm font-semibold bg-gray-100 px-3.5 py-2.5 rounded-full active:scale-[0.96] transition-transform"
        >
          <Phone size={15} /> {ru.calls.title}
        </button>
      </div>
      <div>
        {chats.map((chat) => {
          const isGlobal = chat.type === "global";
          const name = isGlobal ? ru.chats.global : chat.peer?.name;
          const isOnline = !isGlobal && (online[chat.peer?.id] ?? chat.peer?.online);
          return (
            <button
              key={chat.id}
              data-testid={`chat-row-${chat.id}`}
              onClick={() => navigate(`/chats/${chat.id}`)}
              className="w-full flex items-center gap-3 px-6 py-3.5 text-left active:bg-gray-50 transition-colors"
            >
              <div className="relative shrink-0">
                {isGlobal ? (
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                    <Users size={22} className="text-white" />
                  </div>
                ) : (
                  <Avatar avatar={chat.peer?.avatar} name={name} size={48} />
                )}
                {isOnline && (
                  <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-[#34C759] border-2 border-white rounded-full" data-testid={`online-dot-${chat.peer?.id}`} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-bold truncate">{name}</p>
                  {chat.last_message_at && (
                    <span className="text-xs text-gray-400 shrink-0">{dayjs(chat.last_message_at).fromNow(true)}</span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-gray-500 truncate">{preview(chat, user?.id)}</p>
                  {chat.unread > 0 && (
                    <span data-testid={`unread-badge-${chat.id}`} className="bg-black text-white text-[11px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center shrink-0">
                      {chat.unread > 99 ? "99+" : chat.unread}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
